/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import dayjs from 'dayjs'
import { useI18n } from '@shopify/react-i18n'

import List from '../../../../components/List'
import { TeamCell } from '../../../Teams/TeamList'
import DivisionTeamsTotalPaid from './DivisionTeamsTotalPaid'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from '../../../../state'
import { useCallback, useMemo, useState } from 'react'
import Icon from '../../../../components/Icon'
import Select from 'react-select'
import { SideModal, sideModalRef } from './SideModal'

const regStatusOption = [
  {
    label: 'Pending',
    value: 0,
  },
  {
    label: 'Approved',
    value: 1,
  },
  {
    label: 'Approved w/ conditions',
    value: 3,
  },
  {
    label: 'Cancelled',
    value: 2,
  },
]

const InfoButton = ({ key, item }) => {
  const [showInfo, setShowInfo] = useState(false)
  return (
    <>
      <div
        onClick={() => {
          if (showInfo) {
            setShowInfo(false)
          }
        }}
        css={css`
          background-color: ${showInfo && 'rgba(0,0,0,0.5)'};
          position: ${showInfo && 'fixed'};
          z-index: ${showInfo && '999999999'};
          top: ${showInfo && '0'};
          right: ${showInfo && '0'};
          bottom: ${showInfo && '0'};
          left: ${showInfo && '0'};
          transition: background-color 0.09s ease-in-out;
        `}
      />
      <button
        key={key}
        onClick={() => setShowInfo(!showInfo)}
        css={css`
          position: relative;
        `}
      >
        <Icon name='info-circle' color={colors.DEFAULT_FLAIR} fontSize={18} />
        <div
          css={css`
            display: ${showInfo ? 'flex' : 'none'};
            position: absolute;
            flex-direction: column;
            right: 20px;
            top: 10px;
            min-width: 220px;
            min-height: 100px;
            background-color: ${colors.HEADER};
            border-radius: 5px 0px 5px 5px;
            border: 1px solid ${colors.SOFT_STEEL};
            padding: 10px;
            z-index: 999999999;
            box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, 0.75);
            align-items: flex-start;
          `}
        >
          <p
            css={css`
              color: ${colors.WHITE};
              font-weight: bold;
              padding-bottom: 5px;
            `}
          >
            Condition Description:
          </p>

          <p
            css={css`
              color: ${colors.WHITE};
            `}
          >
            {item?.conditions}
          </p>
        </div>
      </button>
    </>
  )
}

const DivisionTeams = observer(({ teams, divisionId }) => {
  const {
    registrationTypes: {
      state: {
        condition_type,
      }
    },
    registration: {
      state: { divisions, registration },
      updateTeamRegStatus,
    },
  } = useMobxStore()

  const [i18n] = useI18n()

  const currentDivision = useMemo(() => {
    return divisions.find((d) => d.id === divisionId)
  }, [divisionId])

  const onRegStatusChange = useCallback(({ value, team }) => {
    updateTeamRegStatus({
      scheduleId: registration?.schedule_id,
      scheduleRegistrationId: currentDivision?.registration?.id,
      status: value,
      teamId: team.uid,
      conditions: null,
      conditionType: null,
      teamRosterId: team.team_roster_id,
    })
  }, [])

  const headers = [
    {
      text: t('common:team'),
      sizePercent: 30,
      sort: 'team.name',
      Cell: TeamCell,
      to: (item) => `${ROUTES.TEAM_ROOT}/${item.uid}/billing`,
    },
    {
      text: t('common:player', { count: 2 }),
      sizePercent: 14,
      minWidth: 70,
      accessor: (item) =>
        `${item?.players_count}/${currentDivision?.registration?.max_players_per_team}`,
    },
    {
      text: 'Due',
      sizePercent: 18,
      minWidth: 70,
      accessor: (item) => {
        const nextInstallmentToPay = item.billing_schedule.find((item) => {
          return item.paid === false
        })

        const due = nextInstallmentToPay?.due_date
        const day = dayjs(due, 'YYYY-MM-DD')
        const isAfter = dayjs().isAfter(day)
        return (
          <div
            title={day.format('D MMMM, YYYY')}
            css={css`
              ${isAfter && `color: ${colors.ERROR_LIGHT};`}
            `}
          >
            {dayjs(nextInstallmentToPay?.due_date).format('MMM D, YYYY')}
          </div>
        )
      },
    },
    {
      text: 'Dep. Paid',
      sizePercent: 12,
      minWidth: 20,
      accessor: (item) => {
        const deposit = item.billing_schedule.find((item) => {
          return item.payment_type === 'deposit'
        })

        return (
          <Icon
            name={deposit?.paid ? 'check' : 'times'}
            color={deposit?.paid ? colors.GREEN : colors.RED}
          />
        )
      },
    },
    {
      text: 'Outstanding',
      sizePercent: 18,
      minWidth: 70,
      accessor: (item) => {
        const teamCost = currentDivision?.registration?.team_cost ?? 0
        const deposit = currentDivision?.registration?.deposit ?? 0

        let totalPaid = 0

        item.billing_schedule.forEach((item) => {
          if (item.paid) {
            totalPaid += item.amount
          }
        })

        return (
          <span>
            {i18n.formatCurrency(teamCost - totalPaid, {
              currency: currentDivision?.registration?.currency?.name,
              form: 'short',
            })}
          </span>
        )
      },
    },
    {
      text: 'Paid',
      sizePercent: 18,
      minWidth: 100,
      Cell: ({ item }) => (
        <DivisionTeamsTotalPaid item={item} currentDivision={currentDivision} />
      ),
    },
    {
      text: 'Status',
      sizePercent: 20,
      sort: 'registration_status',
      minWidth: 98,
      accessor: (item) => {
        return (
          <Select
            options={regStatusOption}
            defaultValue={regStatusOption.find(
              (i) => i.value === item?.registration_status
            )}
            value={regStatusOption.find(
              (i) => i.value === item?.registration_status
            )}
            onChange={({ value }) => {
              const currentValue = regStatusOption.find(
                (i) => i.value === item?.registration_status
              )?.value
              if (value === currentValue) {
                return
              }

              if (value === 3) {
                sideModalRef.current.handleOpen(
                  item,
                  divisionId,
                  registration?.schedule_id,
                  currentDivision?.registration?.id
                )
              } else {
                onRegStatusChange({ value, team: item })
              }
            }}
            styles={{
              container: (baseStyles) => ({
                ...baseStyles,
                width: 150,
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: colors.HEADER,
                borderColor: colors.SOFT_STEEL,
                maxWidth: '100%',
              }),
              singleValue: (baseStyles) => ({
                ...baseStyles,
                color: colors.WHITE,
                fontSize: 12,
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: colors.HEADER,
                borderColor: colors.SOFT_STEEL,
                zIndex: 999999999999999,
              }),
              option: (baseStyles, { isFocused }) => ({
                ...baseStyles,
                backgroundColor: isFocused ? colors.DEFAULT_FLAIR : undefined,
                fontSize: 14,
              }),
            }}
          />
        )
      },
    },
  ]

  const options = (item) => {
    return [
      item.conditions && (
        <InfoButton key={Math.random().toString()} item={item} />
      ),
    ]
  }

  return (
    <>
      <List
        headers={headers}
        removeOverflow
        options={options}
        listProps={{
          items: teams,
        }}
        fullNoItemsText={' '}
      />
      <SideModal
        onConfirm={updateTeamRegStatus}
        divisions={divisions}
        ref={sideModalRef}
        conditionType={condition_type}
      />
    </>
  )
})

export default DivisionTeams
